/**
 * Derivation registry — mirror of the C# PrepareConditions logic.
 * Each derivation takes the full form DTO and computes a value used in conditional rules.
 *
 * Add new derivations here when DB rules reference a NEW computed field. Otherwise this
 * file never needs editing — existing derivations cover Age, PRP*, HomeLess, etc.
 */

export type DerivationFn = (dto: any) => any;

function readPath(obj: any, path: string): any {
    if (!obj) return undefined;
    if (obj[path] !== undefined) return obj[path];
    const lower = path.charAt(0).toLowerCase() + path.slice(1);
    if (obj[lower] !== undefined) return obj[lower];
    const upper = path.charAt(0).toUpperCase() + path.slice(1);
    if (obj[upper] !== undefined) return obj[upper];
    return undefined;
}

function calcAge(dob: any): number {
    if (!dob) return 0;
    let born: Date;
    if (dob instanceof Date) born = dob;
    else if (typeof dob === 'object' && typeof (dob as any).toJSDate === 'function') born = (dob as any).toJSDate();
    else born = new Date(dob);
    if (isNaN(born.getTime())) return 0;
    const today = new Date();
    let age = today.getFullYear() - born.getFullYear();
    const m = today.getMonth() - born.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < born.getDate())) age--;
    return age < 0 ? 0 : age;
}

function getPrograms(dto: any): any[] {
    return readPath(dto, 'programEnrollmentInfo')
        ?? readPath(dto, 'ProgramEnrollmentInfo')
        ?? readPath(dto, 'programEnrollmentinfo')
        ?? [];
}

function activePrograms(dto: any): number[] {
    return getPrograms(dto)
        .filter((p: any) => p && (p.status === 1 || p.Status === 1))
        .map((p: any) => p.program ?? p.Program)
        .filter((p: any) => p != null);
}

function parseLevelOfCare(loc: any): string[] {
    if (!loc) return [];
    if (Array.isArray(loc)) return loc.map(String);
    if (typeof loc === 'string') {
        try {
            const parsed = JSON.parse(loc);
            if (Array.isArray(parsed)) return parsed.map(String);
        } catch {
            return loc.split(',').map(s => s.trim());
        }
    }
    return [];
}

export const DERIVATIONS: Record<string, DerivationFn> = {
    Age: (dto) => calcAge(readPath(dto, 'dob') ?? readPath(dto, 'DOB')),
    age: (dto) => DERIVATIONS.Age(dto),

    PRPCheck: (dto) => {
        const age = DERIVATIONS.Age(dto);
        return age >= 18 && activePrograms(dto).includes(1);
    },

    PRPExists: (dto) => {
        const age = DERIVATIONS.Age(dto);
        const aps = activePrograms(dto);
        return age >= 18 && (aps.includes(1) || aps.includes(2));
    },

    PrpLevel: (dto) => DERIVATIONS.PRPExists(dto),

    RRPOnlyLevel: (dto) => {
        const levels = parseLevelOfCare(
            readPath(readPath(dto, 'riskserviceInfo') ?? readPath(dto, 'RiskserviceInfo') ?? {}, 'levelOfCare')
        );
        return levels.includes('2');
    },

    HomeLess: (dto) => readPath(dto, 'homeLess') ?? readPath(dto, 'HomeLess') ?? false,

    ResidentialStatus: (dto) => {
        const h = readPath(dto, 'housinginfo') ?? readPath(dto, 'Housinginfo');
        const v = h ? (h.residentialStatus ?? h.ResidentialStatus) : null;
        return v != null ? String(v) : '';
    },

    OkwithcurrentSituation: (dto) => {
        const h = readPath(dto, 'housinginfo') ?? readPath(dto, 'Housinginfo');
        const v = h ? (h.okwithcurrentSituation ?? h.OkwithcurrentSituation) : null;
        return v != null ? String(v) : '';
    },

    FirstSourceOfIncome: (dto) => {
        const e = readPath(dto, 'educationEmployementinfo')
            ?? readPath(dto, 'EducationEmployementinfo')
            ?? readPath(dto, 'EducationInfo');
        return e ? String(e.firstSourceOfIncome ?? e.FirstSourceOfIncome ?? 0) : '0';
    },

    SecSourceOfIncome: (dto) => {
        const e = readPath(dto, 'educationEmployementinfo')
            ?? readPath(dto, 'EducationEmployementinfo')
            ?? readPath(dto, 'EducationInfo');
        return e ? String(e.secSourceOfIncome ?? e.SecSourceOfIncome ?? 0) : '0';
    },
};

/**
 * Build the condition-context dictionary from a form DTO.
 * Includes all derived fields, plus raw DTO fields under their original casing.
 */
export function buildConditions(dto: any): Record<string, any> {
    const ctx: Record<string, any> = {};
    for (const [key, fn] of Object.entries(DERIVATIONS)) {
        try { ctx[key] = fn(dto); } catch { ctx[key] = undefined; }
    }
    if (dto && typeof dto === 'object') {
        for (const [k, v] of Object.entries(dto)) {
            if (!(k in ctx) && (typeof v !== 'object' || v == null || v instanceof Date)) {
                ctx[k] = v;
            }
        }
    }
    return ctx;
}
